import { useState } from 'react'
import DeleteButton from './DeleteButton.jsx'

/**
 * FlashcardItem component displays a single flashcard.
 * Handles reveal/hide answer interaction, editing, and deletion.
 *
 * @param {Object} props - Component props
 * @param {Object} props.card - Flashcard data
 * @param {string} props.card.id - Flashcard ID
 * @param {string} props.card.question - Question text
 * @param {string} props.card.answer - Answer text
 * @param {boolean} props.isRevealed - Whether answer is revealed
 * @param {Function} props.onReveal - Callback to reveal answer
 * @param {Function} props.onUpdate - Callback to update flashcard
 * @param {Function} props.onDelete - Callback to delete flashcard
 * @param {boolean} props.isDeleting - Whether deletion is in progress
 * @param {boolean} props.isUpdating - Whether update is in progress
 * @returns {React.ReactElement} Flashcard item
 */
export default function FlashcardItem({
  card,
  isRevealed,
  onReveal,
  onUpdate,
  onDelete,
  isDeleting,
  isUpdating,
}) {
  // Local state for edit mode
  const [isEditing, setIsEditing] = useState(false)
  const [editedQuestion, setEditedQuestion] = useState(card.question)
  const [editedAnswer, setEditedAnswer] = useState(card.answer)
  const [editError, setEditError] = useState(null)

  // Handle card click to reveal answer (only when not editing)
  const handleCardClick = () => {
    if (!isRevealed && !isDeleting && !isEditing && !isUpdating) {
      onReveal(card.id)
    }
  }

  // Handle edit button click (stop propagation to avoid revealing)
  const handleEditClick = (event) => {
    event.stopPropagation()
    setIsEditing(true)
    setEditError(null)
    // Reset edited values to current card values
    setEditedQuestion(card.question)
    setEditedAnswer(card.answer)
  }

  // Handle delete button click (stop propagation to avoid revealing)
  const handleDelete = (event) => {
    event.stopPropagation()
    onDelete(card.id)
  }

  // Handle cancel edit
  const handleCancelEdit = () => {
    setIsEditing(false)
    setEditError(null)
    // Reset edited values
    setEditedQuestion(card.question)
    setEditedAnswer(card.answer)
  }

  // Handle form submit
  const handleUpdateSubmit = async (event) => {
    event.preventDefault()
    setEditError(null)

    // Validate: check if fields are not empty (trim whitespace)
    const trimmedQuestion = editedQuestion.trim()
    const trimmedAnswer = editedAnswer.trim()

    if (!trimmedQuestion || !trimmedAnswer) {
      setEditError('Pytanie i odpowiedź nie mogą być puste.')
      return
    }

    // Call update callback
    const result = await onUpdate(card.id, {
      question: trimmedQuestion,
      answer: trimmedAnswer,
    })

    // If successful, exit edit mode
    if (result.success) {
      setIsEditing(false)
      setEditError(null)
    } else {
      // Show error message from the hook
      setEditError(result.error || 'Nie udało się zaktualizować fiszki.')
    }
  }

  // Render edit form when in edit mode
  if (isEditing) {
    return (
      <article className="flashcard-item flashcard-item--editing">
        <form
          onSubmit={handleUpdateSubmit}
          className="flashcard-item__edit-form"
        >
          {/* Question textarea */}
          <div className="flashcard-item__edit-field">
            <label htmlFor={`question-${card.id}`} className="u-sr-only">
              Pytanie
            </label>
            <textarea
              id={`question-${card.id}`}
              className="flashcard-item__edit-textarea"
              value={editedQuestion}
              onChange={(e) => setEditedQuestion(e.target.value)}
              placeholder="Wpisz pytanie..."
              rows={3}
              disabled={isUpdating}
              aria-label="Edytuj pytanie"
            />
          </div>

          {/* Answer textarea */}
          <div className="flashcard-item__edit-field">
            <label htmlFor={`answer-${card.id}`} className="u-sr-only">
              Odpowiedź
            </label>
            <textarea
              id={`answer-${card.id}`}
              className="flashcard-item__edit-textarea"
              value={editedAnswer}
              onChange={(e) => setEditedAnswer(e.target.value)}
              placeholder="Wpisz odpowiedź..."
              rows={3}
              disabled={isUpdating}
              aria-label="Edytuj odpowiedź"
            />
          </div>

          {/* Error message */}
          {editError && (
            <p className="flashcard-item__edit-error" role="alert">
              {editError}
            </p>
          )}

          {/* Action buttons */}
          <div className="flashcard-item__edit-actions">
            <button
              type="submit"
              className="flashcard-item__edit-save"
              disabled={isUpdating}
              aria-label="Zapisz zmiany"
            >
              {isUpdating ? 'Zapisywanie...' : 'Zapisz'}
            </button>
            <button
              type="button"
              className="flashcard-item__edit-cancel"
              onClick={handleCancelEdit}
              disabled={isUpdating}
              aria-label="Anuluj edycję"
            >
              Anuluj
            </button>
          </div>
        </form>
      </article>
    )
  }

  // Render normal view when not editing
  return (
    <article
      className={`flashcard-item ${isRevealed ? 'flashcard-item--revealed' : ''} ${isDeleting ? 'flashcard-item--deleting' : ''}`}
      onClick={handleCardClick}
      role="button"
      tabIndex={isDeleting || isUpdating ? -1 : 0}
      aria-label={`Fiszka: ${card.question}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          handleCardClick()
        }
      }}
    >
      {/* Question block */}
      <div className="flashcard-item__question">
        <h3 className="flashcard-item__question-text">{card.question}</h3>
      </div>

      {/* Answer block (conditionally revealed) */}
      {isRevealed && (
        <div className="flashcard-item__answer">
          <p className="flashcard-item__answer-text">{card.answer}</p>
        </div>
      )}

      {/* Action buttons */}
      <div className="flashcard-item__actions">
        {/* Edit button */}
        <button
          className="flashcard-item__edit-button"
          onClick={handleEditClick}
          disabled={isDeleting || isUpdating}
          aria-label={`Edytuj fiszkę: ${card.question}`}
          title="Edytuj"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
          </svg>
        </button>

        {/* Delete button */}
        <DeleteButton
          onClick={handleDelete}
          isDeleting={isDeleting}
          label={`Usuń fiszkę: ${card.question}`}
        />
      </div>
    </article>
  )
}
